package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

@Page("/kmm/secure_storage")
@Composable
fun SecureStorageKMPPage() {
    BlogLayout(
        PageProperties(
            "Secure Storage",
            ""
        ),
        BlogProperties("KMP", "Secure Storage")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Secure Storage",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //commonMain
                    
                    interface IKSecureStorage {
                        /**
                         * Writes a [String] to the disk, using SharedPreferences or KeyChain, depending on the
                         * platform.
                         *
                         * @param key [String] key that is associated to the value.
                         * @param value [String] value to store.
                         */
                        fun setItem(key: String, value: String?)

                        /**
                         * Retrieves a [String] from the disk, using SharedPreferences or KeyChain, depending on the
                         * platform.
                         *
                         * @param key [String] key that is associated to a value.
                         * @return A [String] value if the key/value pair exists, otherwise null.
                         */
                        fun getItem(key: String): String?

                        /**
                         * Deletes a [String] from the disk, using SharedPreferences or KeyChain, depending on the
                         * platform.
                         *
                         * @param key [String] key that is associated to a value.
                         */
                        fun removeItem(key: String)

                        /**
                         * Clears all data from disk, using SharedPreferences or KeyChain, depending on the platform.
                         */
                        fun clear()
                    }
                    
                    import kotlinx.serialization.DeserializationStrategy
                    import kotlinx.serialization.encodeToString
                    import kotlinx.serialization.json.Json
                    import kotlinx.serialization.serializer

                    @Suppress("EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
                    expect class KSecureStorage() : IKSecureStorage

                    /**
                     * Writes a [Boolean] to the disk, using SharedPreferences or KeyChain, depending on the platform.
                     *
                     * @param key [String] key that is associated to the value.
                     * @param value [Boolean] value to store.
                     */
                    fun IKSecureStorage.setItem(key: String, value: Boolean) {
                        setItem(key, value.toString())
                    }

                    /**
                     * Writes a [Number] to the disk, using SharedPreferences or KeyChain, depending on the platform.
                     *
                     * @param key [String] key that is associated to the value.
                     * @param value [Number] value to store.
                     */
                    fun IKSecureStorage.setItem(key: String, value: Number) {
                        setItem(key, value.toString())
                    }

                    /**
                     * Writes a [T] to the disk, using SharedPreferences or KeyChain, depending on the platform.
                     *
                     * @param key [String] key that is associated to the value.
                     * @param value [T] value to store.
                     */
                    inline fun <reified T> IKSecureStorage.setItem(key: String, value: T) {
                        setItem(key, Json.encodeToString(value))
                    }

                    /**
                     * Retrieves data from the disk, using SharedPreferences or KeyChain, depending on the platform
                     * and returns a [Boolean] if the key/value pair exists.
                     *
                     * @param key [String] value that is associated to a value.
                     * @return True if the key/value pair exists, otherwise false.
                     */
                    fun IKSecureStorage.hasItem(key: String): Boolean {
                        return getItem(key) != null
                    }

                    /**
                     * Retrieves data from the disk, using SharedPreferences or KeyChain, depending on the platform
                     * and return a [Boolean] if the key/value pair exists, otherwise null.
                     *
                     * @param key [String] value that is associated to a value.
                     * @return [Boolean] value if the key/value pair exists, otherwise null.
                     */
                    fun IKSecureStorage.getBoolean(key: String): Boolean? {
                        return getItem(key)?.toBooleanStrictOrNull()
                    }

                    /**
                     * Retrieves data from the disk, using SharedPreferences or KeyChain, depending on the platform
                     * and return a [Int] if the key/value pair exists, otherwise null.
                     *
                     * @param key [String] value that is associated to a value.
                     * @return [Int] value if the key/value pair exists, otherwise null.
                     */
                    fun IKSecureStorage.getInt(key: String): Int? {
                        return getItem(key)?.toIntOrNull()
                    }

                    /**
                     * Retrieves data from the disk, using SharedPreferences or KeyChain, depending on the platform
                     * and return a [Long] if the key/value pair exists, otherwise null.
                     *
                     * @param key [String] value that is associated to a value.
                     * @return [Long] value if the key/value pair exists, otherwise null.
                     */
                    fun IKSecureStorage.getLong(key: String): Long? {
                        return getItem(key)?.toLongOrNull()
                    }

                    /**
                     * Retrieves data from the disk, using SharedPreferences or KeyChain, depending on the platform
                     * and return a [Float] if the key/value pair exists, otherwise null.
                     *
                     * @param key [String] value that is associated to a value.
                     * @return [Float] value if the key/value pair exists, otherwise null.
                     */
                    fun IKSecureStorage.getFloat(key: String): Float? {
                        return getItem(key)?.toFloatOrNull()
                    }

                    /**
                     * Retrieves data from the disk, using SharedPreferences or KeyChain, depending on the platform
                     * and return a [Double] if the key/value pair exists, otherwise null.
                     *
                     * @param key [String] value that is associated to a value.
                     * @return [Double] value if the key/value pair exists, otherwise null.
                     */
                    fun IKSecureStorage.getDouble(key: String): Double? {
                        return getItem(key)?.toDoubleOrNull()
                    }

                    /**
                     * This function is doing the same as [KSecureStorage.getItem].
                     *
                     * @param key [String] value that is associated to a value.
                     * @return [String] value if the key/value pair exists, otherwise null.
                     */
                    fun IKSecureStorage.getString(key: String): String? {
                        return getItem(key)
                    }

                    /**
                     * Retrieves data from the disk, using SharedPreferences or KeyChain, depending on the platform
                     * and return a [T] if the key/value pair exists, otherwise null.
                     *
                     * @param key [String] value that is associated to a value.
                     * @param jsonDeserializer Optional [DeserializationStrategy] to deserialize the JSON string.
                     * @return Generic object [T] value if the key/value pair exists, otherwise null.
                     */
                    inline fun <reified T> IKSecureStorage.getTypedObject(
                        key: String,
                        jsonDeserializer: DeserializationStrategy<T>? = null
                    ): T? {
                        return getItem(key)?.let { jsonValue ->
                            Json.decodeFromString(
                                jsonDeserializer ?: Json.serializersModule.serializer(),
                                jsonValue
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //iosMain
                    
                    import kotlinx.cinterop.*
                    import platform.CoreFoundation.*
                    import platform.Foundation.*
                    import platform.Security.*

                    @OptIn(ExperimentalForeignApi::class, BetaInteropApi::class)
                    actual class KSecureStorage actual constructor() : IKSecureStorage {

                        override fun setItem(key: String, value: String?) {
                            val valueAsNSString = value?.run {
                                NSString.create(string = this).dataUsingEncoding(NSUTF8StringEncoding)
                            }

                            val query = query(
                                kSecClass to kSecClassGenericPassword,
                                kSecAttrAccount to CFBridgingRetain(key),
                                kSecValueData to CFBridgingRetain(valueAsNSString)
                            )

                            SecItemDelete(query)
                            val status = SecItemAdd(query, null)

                            if (status == errSecDuplicateItem) {
                                throw Exception("Item with key already exists")
                            } else if (status != errSecSuccess) {
                                throw Exception("Error adding item with key")
                            }
                        }

                        override fun getItem(key: String): String? {
                            val getQuery = query(
                                kSecClass to kSecClassGenericPassword,
                                kSecAttrAccount to CFBridgingRetain(key),
                                kSecReturnData to kCFBooleanTrue,
                                kSecMatchLimit to kSecMatchLimitOne
                            )

                            memScoped {
                                val result = alloc<CFTypeRefVar>()
                                val status = SecItemCopyMatching(getQuery, result.ptr)

                                if (status == errSecItemNotFound) {
                                    return null
                                } else if (status != errSecSuccess) {
                                    throw Exception("Error getting item with key")
                                } else {
                                    val data = CFBridgingRelease(result.value) as NSData
                                    return NSString.create(data, NSUTF8StringEncoding).toString()
                                }
                            }
                        }

                        override fun removeItem(key: String) {
                            val deleteQuery = query(
                                kSecClass to kSecClassGenericPassword,
                                kSecAttrAccount to CFBridgingRetain(key),
                                kSecReturnData to kCFBooleanTrue,
                            )

                            val status = SecItemDelete(deleteQuery)

                            if (status != errSecSuccess) {
                                throw Exception("Error deleting item with key and status")
                            }
                        }

                        override fun clear() {
                            val query = query(
                                kSecClass to kSecClassGenericPassword
                            )

                            val status = SecItemDelete(query)

                            if (status != errSecSuccess) {
                                throw Exception("Error clearing items with status")
                            }
                        }
                    }

                    @OptIn(ExperimentalForeignApi::class)
                    private fun query(vararg pairs: Pair<CFStringRef?, CFTypeRef?>): CFMutableDictionaryRef? {
                        val map = mapOf(*pairs)
                        return CFDictionaryCreateMutable(
                            null, pairs.size.convert(), null, null
                        ).apply {
                            map.forEach { CFDictionaryAddValue(this, it.key, it.value) }
                        }.apply {
                            CFAutorelease(this)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //androidMain
                    
                    import android.content.Context
                    import android.content.SharedPreferences
                    import android.util.Log
                    import androidx.annotation.VisibleForTesting
                    import androidx.security.crypto.EncryptedSharedPreferences
                    import androidx.security.crypto.MasterKey

                    private const val SHARED_PREFERENCES_FILENAME = "K_SECURE_STORAGE_ENCRYPTED_STORAGE_SHARED_PREF"

                    actual class KSecureStorage : IKSecureStorage {

                        private var sharedPreferences: SharedPreferences? = null

                        actual constructor() : this(applicationContext)

                        @VisibleForTesting(otherwise = VisibleForTesting.PRIVATE)
                        constructor(context: Context) {
                            try {
                                val key = MasterKey.Builder(context)
                                    .setKeyScheme(MasterKey.KeyScheme.AES256_GCM)
                                    .build()
                                sharedPreferences = EncryptedSharedPreferences.create(
                                    context,
                                    SHARED_PREFERENCES_FILENAME,
                                    key,
                                    EncryptedSharedPreferences.PrefKeyEncryptionScheme.AES256_SIV,
                                    EncryptedSharedPreferences.PrefValueEncryptionScheme.AES256_GCM
                                )
                            } catch (ex: Exception) {
                                Log.e(
                                    KSecureStorage::class.simpleName,
                                    "Failed to create encrypted shared preferences! Failing back to standard SharedPreferences",
                                    ex
                                )
                                sharedPreferences = context.getSharedPreferences(
                                    SHARED_PREFERENCES_FILENAME,
                                    Context.MODE_PRIVATE
                                )
                            }
                        }

                        private fun getSharedPreferences(): SharedPreferences {
                            sharedPreferences?.let {
                                return it
                            } ?: run {
                                throw NullPointerException("Could not initialize SharedPreferences")
                            }
                        }

                        override fun setItem(key: String, value: String?) {
                            val editor = getSharedPreferences().edit()
                            editor.putString(key, value)
                            val saved = editor.commit()
                            if (!saved) {
                                throw Exception("An error occurred while saving key")
                            }
                        }

                        override fun getItem(key: String): String? {
                            return getSharedPreferences().getString(key, null)
                        }

                        override fun removeItem(key: String) {
                            val editor = getSharedPreferences().edit()
                            editor.remove(key)
                            val saved = editor.commit()
                            if (!saved) {
                                throw Exception("An error occurred while removing key")
                            }
                        }

                        override fun clear() {
                            val editor = getSharedPreferences().edit()
                            editor.clear()
                            val saved = editor.commit()
                            if (!saved) {
                                throw Exception("An error occurred while clearing SharedPreferences")
                            }
                        }
                    }
                    
                    import android.content.Context
                    import androidx.startup.Initializer

                    internal lateinit var applicationContext: Context
                        private set

                    object KSecureStorageContext

                    @Suppress("unused")
                    class KSecureStorageInitializer: Initializer<KSecureStorageContext> {
                        override fun create(context: Context): KSecureStorageContext {
                            applicationContext = context.applicationContext
                            return KSecureStorageContext
                        }

                        override fun dependencies(): List<Class<out Initializer<*>>> {
                            return emptyList()
                        }
                    }
                    
                    <manifest xmlns:android="http://schemas.android.com/apk/res/android"
                        xmlns:tools="http://schemas.android.com/tools">

                        <application>
                            <provider
                                android:name="androidx.startup.InitializationProvider"
                                android:authorities="dollarSign{applicationId}.androidx-startup"
                                android:exported="false"
                                tools:node="merge">

                                <meta-data
                                    android:name="io.github.alexandereggers.ksecurestorage.KSecureStorageInitializer"
                                    android:value="androidx.startup" />
                            </provider>
                        </application>

                    </manifest>
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    private val kSecureStorage = KSecureStorage()
                    
                    const val TEST_STORAGE_KEY = "hasDisplayedBefore"
                    val hasDisplayedBefore = kSecureStorage.getBoolean(TEST_STORAGE_KEY) ?: false
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/Junaed29/VendingMachineMultiplatform